"use client";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import {
  TypeMultiPackageTeaser,
  TypePackageDetailItemSkeleton,
  isTypePackageDetailItem,
} from "@contentful/types";
import { faArrowRight } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { notEmpty } from "@yardzen/js-util";
import { Badge } from "@yardzen/ui/components/badge";
import { Button } from "@yardzen/ui/components/button";
import { ContentfulText } from "@yardzen/ui/components/contentful-text";
import { H1 } from "@yardzen/ui/components/h1";
import { H2 } from "@yardzen/ui/components/h2";
import { H3 } from "@yardzen/ui/components/h3";
import { Link } from "@yardzen/ui/components/link";
import { Small } from "@yardzen/ui/components/small";
import { Entry } from "contentful";
import Image from "next/image";

export const MultiPackageTeaserBlocks: React.FC<{
  multiPackageTeaser: TypeMultiPackageTeaser<"WITHOUT_UNRESOLVABLE_LINKS">;
}> = ({ multiPackageTeaser }) => {
  const { moduleHeading, moduleSubheading, packages } =
    multiPackageTeaser?.fields || {};

  return (
    <div className="mt-5 flex flex-col items-center justify-center px-1 pt-8 max-md:mt-4 md:px-8 md:pt-8">
      <div className="flex flex-col items-center justify-center gap-4 p-5">
        <H1>{moduleHeading}</H1>
        {moduleSubheading && (
          <div className="text-moss-greens-90 text-center">
            {documentToReactComponents(moduleSubheading)}
          </div>
        )}
      </div>

      <div className="flex w-full max-w-7xl flex-col items-start justify-center gap-4 md:flex-row md:items-stretch">
        {packages
          ?.filter(notEmpty)
          .filter(isTypePackageDetailItem)
          .map((package_card) => (
            <PackageCard
              key={package_card?.sys.id}
              keyVal={package_card?.sys.id}
              package_card={package_card}
            />
          ))}
      </div>
    </div>
  );
};

const PackageCard = ({
  package_card,
  keyVal,
}: {
  package_card: Entry<
    TypePackageDetailItemSkeleton,
    "WITHOUT_UNRESOLVABLE_LINKS"
  >;
  keyVal: string;
}) => {
  const {
    packagePath,
    heading,
    previewImage,
    price,
    promoPrice,
    detailCardFlag,
    detailCardPill,
    checkoutDetails,
  } = package_card?.fields || {};
  const host = process.env.NEXT_PUBLIC_PROXY_BASE_URL;
  const showPromoPrice = promoPrice && promoPrice !== price;
  return (
    <div
      key={keyVal}
      className="bg-brand-beige-light flex w-full flex-col rounded-xl shadow-md md:max-w-md"
    >
      {detailCardFlag && (
        <div className="bg-brand-tertiary-30 flex w-full items-center justify-center p-2">
          <Small>{detailCardFlag}</Small>
        </div>
      )}
      <div className="flex flex-col items-start justify-center gap-4 p-6">
        <div className="flex w-full flex-col items-center gap-4">
          <div className="flex w-full flex-row items-center justify-between gap-4 md:flex-col lg:flex-row">
            <div className="flex flex-row items-center gap-2">
              <H2 className="!pb-0">{heading}</H2>
              {detailCardPill && (
                <div className="flex-0">
                  <Badge variant="zest">{detailCardPill}</Badge>
                </div>
              )}
            </div>
            <div className="text-brand-moss-greens-100">
              {showPromoPrice ? (
                <div className="flex flex-row gap-1">
                  <H3 className="line-through">${price}</H3>
                  <H3 className="text-brand-primary-darker">${promoPrice}</H3>
                </div>
              ) : (
                <b className="text-brand-primary-darker">${price}</b>
              )}
            </div>
          </div>
        </div>
        <div className="relative h-full w-full overflow-hidden rounded-lg sm:max-h-[200px]">
          {previewImage?.fields.image?.fields.file?.url && (
            <Image
              src={`https:${previewImage?.fields.image?.fields.file?.url}`}
              alt={previewImage?.fields.image?.fields.file?.fileName}
              width={435}
              height={565}
              style={{
                objectFit: "cover",
                width: "100%",
                height: "100%",
              }}
            />
          )}
        </div>
        <div className="flex w-full flex-col gap-4">
          {checkoutDetails && (
            <div className="flex flex-col gap-2">
              <ContentfulText
                content={checkoutDetails}
                className="text-brand-primary-dark font-body border-system-grayscale-20 text-md mb-2 rounded-lg border border-dashed p-2 text-left font-normal"
                pClassName="mb-0 font-semibold"
                ulClassName="ml-6"
              ></ContentfulText>
            </div>
          )}
        </div>
      </div>
      <div className="mt-auto flex flex-none flex-col items-center justify-center px-4 pb-4 pt-2">
        <Button className="w-full" asChild>
          <Link href={`${host}/${packagePath}`}>
            Get Started <FontAwesomeIcon icon={faArrowRight} />
          </Link>
        </Button>
      </div>
    </div>
  );
};
